import { useState } from "react";
import { arktypeResolver } from "@hookform/resolvers/arktype";
import { Sparkles, X } from "lucide-react";
import { useForm, useWatch } from "react-hook-form";

import type { PersonDetail } from "@/api/db/people";
import { SignatureRecipient } from "@/api/signature/recipient";
import { OverlayShell } from "@/components/ui/overlay";
import { useSignatureSend } from "@/hooks/use-signatures";
import { useSendRequirements, useTermList } from "@/hooks/use-terms";
import { DocumentosLabels } from "@/lib/labels/documentos";
import {
	SignaturePreparation,
	SignaturePreparationSchemas,
	type SignaturePreparationForm,
} from "@/routes/_app/pessoa/-utils/signature-preparation";

import { SendTermsFooter } from "./footer";
import { SendTermsAssistant } from "./assistant";
import { SendTermsFields } from "./fields";
import { SendTermsPreview } from "./preview";
import { SendTermsPatterns } from "./patterns";
import { SendTermsRecipient } from "./recipient";
import { SendTermsSelection } from "./terms";

export function SendTermsModal({ person, onClose }: { person: PersonDetail; onClose: () => void }) {
	const { data: terms } = useTermList();
	const { mutate: send, isPending } = useSignatureSend();
	const [selected, setSelected] = useState<Set<number>>(new Set());
	const { control, register, handleSubmit, setValue } = useForm<SignaturePreparationForm>({
		resolver: arktypeResolver(SignaturePreparationSchemas.form),
		mode: "onChange",
		defaultValues: { deadlineDays: 30, customValues: {}, patternValues: {} },
	});
	const customValues = useWatch({ control, name: "customValues" });
	const patternValues = useWatch({ control, name: "patternValues" });
	const deadlineDays = useWatch({ control, name: "deadlineDays" });
	const termIds = [...selected];
	const requirements = useSendRequirements(termIds);
	const requirementList = requirements.data ?? [];
	const selectedTerms = terms?.filter((term) => selected.has(term.id)) ?? [];
	const recipient = SignatureRecipient.prepare(person);
	const recipientReady = recipient.blockers.length === 0;
	const requirementsPending = selected.size > 0 && requirements.data === undefined;
	const plainFieldTotal = requirementList.reduce(
		(total, requirement) => total + requirement.fields.length,
		0,
	);
	const plainFieldFilled = requirementList.reduce(
		(total, requirement) =>
			total +
			requirement.fields.filter((field) => {
				const key = SignaturePreparation.fieldKey(requirement.termId, field.fieldId);

				return SignaturePreparation.valid(field, customValues[key]);
			}).length,
		0,
	);
	const patternFieldTotal = requirementList.reduce(
		(total, requirement) =>
			total +
			requirement.patterns.reduce(
				(patternTotal, pattern) =>
					patternTotal +
					SignaturePreparation.items(
						patternValues,
						SignaturePreparation.patternKey(requirement.termId, pattern.patternId),
					).length *
						pattern.fields.length,
				0,
			),
		0,
	);
	const patternFieldFilled = requirementList.reduce(
		(total, requirement) =>
			total +
			requirement.patterns.reduce((patternTotal, pattern) => {
				const items = SignaturePreparation.items(
					patternValues,
					SignaturePreparation.patternKey(requirement.termId, pattern.patternId),
				);

				return (
					patternTotal +
					items.reduce(
						(itemTotal, item) =>
							itemTotal +
							pattern.fields.filter((field) =>
								SignaturePreparation.valid(field, item.values[String(field.fieldId)]),
							).length,
						0,
					)
				);
			}, 0),
		0,
	);
	const totalFields = plainFieldTotal + patternFieldTotal;
	const filledFields = plainFieldFilled + patternFieldFilled;
	const allFieldsFilled = filledFields === totalFields;
	const allPatternsFilled = requirementList.every((requirement) =>
		requirement.patterns.every((pattern) =>
			SignaturePreparation.patternComplete(
				pattern,
				SignaturePreparation.items(
					patternValues,
					SignaturePreparation.patternKey(requirement.termId, pattern.patternId),
				),
			),
		),
	);
	const deadlineValid = Number.isInteger(deadlineDays) && deadlineDays >= 1 && deadlineDays <= 365;
	const canSubmit =
		recipientReady &&
		selectedTerms.length > 0 &&
		!requirementsPending &&
		allFieldsFilled &&
		allPatternsFilled &&
		deadlineValid &&
		!isPending;

	const handleToggle = (id: number) => {
		setSelected((current) => {
			const next = new Set(current);

			if (next.has(id)) {
				next.delete(id);

				return next;
			}

			next.add(id);

			return next;
		});
	};

	const handleSend = (form: SignaturePreparationForm) => {
		if (!canSubmit) {
			return;
		}

		send(
			{
				personId: person.id,
				termIds,
				deadlineDays: form.deadlineDays,
				customValues: requirementList.map((requirement) => ({
					termId: requirement.termId,
					values: SignaturePreparation.values(requirement, form.customValues),
					patterns: SignaturePreparation.patternItems(requirement, form.patternValues),
				})),
			},
			{ onSuccess: onClose },
		);
	};

	return (
		<OverlayShell label={DocumentosLabels.sendTitle} onClose={onClose} className="p-3 sm:p-5">
			<form
				data-component="send-terms-modal"
				data-recipient-ready={recipientReady}
				data-selected={selectedTerms.length}
				onSubmit={handleSubmit(handleSend)}
				className="relative mx-auto grid h-full w-full max-w-384 grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden rounded-xl bg-card shadow-overlay"
			>
				<header className="flex items-center justify-between gap-4 border-b border-border px-5 py-4">
					<div className="flex items-center gap-3">
						<div className="flex size-9 items-center justify-center rounded-full bg-primary text-primary-foreground">
							<Sparkles className="size-4" />
						</div>
						<div>
							<h2 className="type-section-title text-foreground">{DocumentosLabels.sendTitle}</h2>
							<p className="text-sm text-muted-foreground">{recipient.name}</p>
						</div>
					</div>

					<button
						type="button"
						aria-label="Fechar"
						onClick={onClose}
						className="rounded-md p-2 text-muted-foreground hover:bg-background hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
					>
						<X className="size-5" />
					</button>
				</header>

				<main className="grid min-h-0 min-w-0 overflow-y-auto lg:grid-cols-[minmax(25rem,0.85fr)_minmax(0,1.15fr)] lg:overflow-hidden">
					<div className="flex min-w-0 flex-col gap-4 bg-background/40 p-4 sm:p-5 lg:overflow-y-auto">
						<SendTermsAssistant
							key={termIds.join("_")}
							personId={person.id}
							terms={selectedTerms}
							requirements={requirementList}
							customValues={customValues}
							patternValues={patternValues}
							setValue={setValue}
							disabled={isPending || requirementsPending}
						/>

						<SendTermsRecipient personId={person.id} recipient={recipient} />

						<SendTermsSelection
							terms={terms}
							selected={selected}
							disabled={isPending}
							onToggle={handleToggle}
						/>

						<SendTermsFields
							terms={selectedTerms}
							requirements={requirementList}
							customValues={customValues}
							control={control}
							pending={requirementsPending}
							disabled={isPending}
						/>

						<SendTermsPatterns
							terms={selectedTerms}
							requirements={requirementList}
							control={control}
							disabled={isPending}
						/>
					</div>

					<SendTermsPreview
						person={person}
						terms={selectedTerms}
						customValues={customValues}
						patternValues={patternValues}
						ready={
							recipientReady &&
							selectedTerms.length > 0 &&
							!requirementsPending &&
							allFieldsFilled &&
							allPatternsFilled
						}
						filledFields={filledFields}
						totalFields={totalFields}
						recipientReady={recipientReady}
					/>
				</main>

				<SendTermsFooter
					documents={selectedTerms.length}
					filledFields={filledFields}
					totalFields={totalFields}
					isPending={isPending}
					canSubmit={canSubmit}
					register={register}
					onClose={onClose}
				/>
			</form>
		</OverlayShell>
	);
}
